import {
  Box,
  Flex,
  Image,
  PartnerLogo,
  Typography as ReeferTypography,
  effects,
  useDesktopMediaQuery,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';

import { useContainerSize } from '@jane/shared/hooks';

import { BlockSkeleton } from '../loadingWrapper/skeletonComponents';
import {
  AdProductCard,
  PlaceHolderAdProductCard,
} from './adProductCard/adProductCard';
import type { CustomImageAdProps } from './customImageAd.types';

import styles from './customImageAd.module.css';

const REFERENCE_WIDTH = 952;
const REFERENCE_HEIGHT = 317;

export const CustomImageAd = ({
  customImageData,
  isKiosk,
  onImageClick,
  typography,
  previewWidth = '100%',
  products,
}: CustomImageAdProps) => {
  const isDesktop = useDesktopMediaQuery({});
  const [imageLoaded, setImageLoaded] = useState(
    customImageData?.imageUrl ? false : true
  );
  const Typography = typography || ReeferTypography;

  const { containerSize, containerRef, refIsMounted } = useContainerSize(
    REFERENCE_WIDTH,
    REFERENCE_HEIGHT
  );

  const imageOverlayRgb = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () =>
      customImageData?.overlayColor
        ? effects.hexToRgb(customImageData.overlayColor)
        : [],
    [customImageData?.overlayColor]
  );

  useEffect(() => {
    products.forEach((product) => {
      product.impressionHandler();
    });
  }, [products.length]);

  if (!customImageData) {
    return null;
  }

  const {
    headline: tagline,
    imageUrl,
    logoUrl: brandLogoUrl,
    overlayColor,
    positionX: imagePositionX,
    positionY: imagePositionY,
    scaleNumber: imageScale,
    textColor,
  } = customImageData;

  const brandName = products[0]?.brand ?? '';

  const actualPositionX =
    (imagePositionX / REFERENCE_WIDTH) * containerSize.width;
  const actualPositionY =
    (imagePositionY / REFERENCE_HEIGHT) * containerSize.height;

  const typographyColor =
    textColor === '#FFFFFF' ? 'grays-white' : 'grays-black';

  return (
    <Box
      className={clsx(styles['adAspectRatio'], {
        [styles['imageLoaded']]: imageLoaded,
        [styles['imageNotLoaded']]: !imageLoaded,
      })}
      position="relative"
      width={previewWidth}
      overflow="hidden"
      borderRadius="lg"
    >
      <div ref={containerRef} className={styles['fullSize']}>
        {refIsMounted && (
          <>
            <button
              className={styles['brandImage']}
              style={{
                transform: `translate(${actualPositionX}px, ${actualPositionY}px) scale(${imageScale})`,
              }}
              onClick={() => onImageClick && onImageClick(brandName)}
            >
              <Image
                src={imageUrl}
                altText={`${brandName} ad`}
                onLoad={() => setImageLoaded(true)}
                objectFit="contain"
                responsive
                height="100%"
                width="100%"
              />
            </button>

            {overlayColor && (
              <Box
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                zIndex={2}
                className={styles['noPointerEvents']}
                style={{
                  backgroundColor: `rgba(${imageOverlayRgb[0]}, ${imageOverlayRgb[1]}, ${imageOverlayRgb[2]}, 0.2)`,
                }}
              />
            )}

            <Flex
              position="relative"
              width="100%"
              height="100%"
              p={isDesktop ? 24 : 12}
              zIndex={3}
              justifyContent="space-between"
              gap={12}
              className={clsx(
                styles['imageWithLoading'],
                styles['noPointerEvents']
              )}
            >
              <Flex alignItems="end" alignSelf="end" gap={8}>
                <PartnerLogo
                  variant="brand"
                  size="lg"
                  name={brandName}
                  image={brandLogoUrl}
                  style={{ background: 'white' }}
                />

                <Flex flexDirection="column">
                  <Typography
                    variant="h2-semibold"
                    color={typographyColor}
                    pb={12}
                    branded
                  >
                    {brandName}
                  </Typography>
                  <Typography
                    variant={isKiosk ? 'small-body-semibold' : 'body-semibold'}
                    color={typographyColor}
                  >
                    {tagline || 'Your brand tagline'}
                  </Typography>
                </Flex>
              </Flex>

              <Flex alignItems="flex-end" height="100%" gap={16}>
                {products.length > 0
                  ? products.map((product) => (
                      <AdProductCard
                        key={product.product_id}
                        isKiosk={isKiosk}
                        product={product}
                        typography={typography}
                      />
                    ))
                  : Array.from({ length: 3 }).map((_, index) => (
                      <PlaceHolderAdProductCard key={index} />
                    ))}
              </Flex>
            </Flex>
          </>
        )}

        {!refIsMounted && (
          <div className={styles['fullSize']} data-testid="block-skeleton">
            <BlockSkeleton height="100%" />
          </div>
        )}
      </div>
    </Box>
  );
};
